import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Car,
  Plus,
  MapPin,
  Clock,
  Calendar,
  Users,
  DollarSign,
  Edit,
  Trash2,
  CheckCircle,
  X,
  RefreshCw,
  ArrowLeft,
  Eye,
  MessageSquare
} from 'lucide-react';

const MyRoutesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [requests, setRequests] = useState([]);

  const [formData, setFormData] = useState({
    route_type: 'school',
    route_name: '',
    start_location: '',
    end_location: '',
    stops: '',
    schedule_days: [],
    departure_time: '',
    return_time: '',
    contribution_type: 'fuel',
    contribution_amount: '',
    contribution_details: '',
    rules: '',
    max_riders: 3
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchRoutes();
  }, [user, navigate]);

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API}/carpool/my-routes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async (routeId) => {
    try {
      const response = await axios.get(`${API}/carpool/requests/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const handleViewRequests = (route) => {
    setSelectedRoute(route);
    fetchRequests(route.id);
  };

  const handleOpenForm = (route = null) => {
    if (route) {
      setEditingRoute(route);
      setFormData({
        route_type: route.route_type,
        route_name: route.route_name,
        start_location: route.start_location,
        end_location: route.end_location,
        stops: route.stops?.join(', ') || '',
        schedule_days: route.schedule_days || [],
        departure_time: route.departure_time,
        return_time: route.return_time || '',
        contribution_type: route.contribution_type,
        contribution_amount: route.contribution_amount || '',
        contribution_details: route.contribution_details || '',
        rules: route.rules || '',
        max_riders: route.max_riders
      });
    } else {
      setEditingRoute(null);
      setFormData({
        route_type: 'school',
        route_name: '',
        start_location: '',
        end_location: '',
        stops: '',
        schedule_days: [],
        departure_time: '',
        return_time: '',
        contribution_type: 'fuel',
        contribution_amount: '',
        contribution_details: '',
        rules: '',
        max_riders: 3
      });
    }
    setShowCreateDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.route_name || !formData.start_location || !formData.end_location || !formData.departure_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        stops: formData.stops ? formData.stops.split(',').map(s => s.trim()) : []
      };

      if (editingRoute) {
        await axios.put(`${API}/carpool/routes/${editingRoute.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Route updated!');
      } else {
        await axios.post(`${API}/carpool/routes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Route created!');
      }
      setShowCreateDialog(false);
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save route');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestAction = async (requestId, status) => {
    try {
      await axios.patch(`${API}/carpool/requests/${requestId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Request ${status}`);
      fetchRequests(selectedRoute.id);
      fetchRoutes();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!confirm('Are you sure you want to deactivate this route?')) return;
    try {
      await axios.delete(`${API}/carpool/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route deactivated');
      fetchRoutes();
    } catch (error) {
      toast.error('Failed to deactivate route');
    }
  };

  const toggleDay = (day) => {
    setFormData(p => ({
      ...p,
      schedule_days: p.schedule_days.includes(day)
        ? p.schedule_days.filter(d => d !== day)
        : [...p.schedule_days, day]
    }));
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/carpool')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Carpool
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Car className="w-6 h-6 text-emerald-500" /> My Carpool Routes
            </h1>
            <p className="text-slate-600">Manage your routes and rider requests</p>
          </div>
          <Button onClick={() => handleOpenForm()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-route-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Route
          </Button>
        </div>

        {routes.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No routes yet</p>
              <Button onClick={() => handleOpenForm()} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Route
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {routes.map((route) => (
              <Card key={route.id} className="bg-white" data-testid={`my-route-${route.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className="bg-emerald-100 text-emerald-700 mb-2">{route.route_type}</Badge>
                      <h3 className="font-heading font-bold text-lg">{route.route_name}</h3>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenForm(route)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDeleteRoute(route.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-emerald-500" />
                      {route.start_location} → {route.end_location}
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> {route.departure_time}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> {route.schedule_days?.join(', ')}
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1 text-sm">
                        <Users className="w-4 h-4 text-emerald-500" />
                        {route.current_riders}/{route.max_riders} riders
                      </span>
                      {route.pending_requests > 0 && (
                        <Badge className="bg-amber-500 text-white">{route.pending_requests} pending</Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/carpool/route/${route.id}`}>
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </Link>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleViewRequests(route)}>
                            <Users className="w-4 h-4 mr-1" /> Requests
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="font-heading">Rider Requests</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                            {requests.length === 0 ? (
                              <p className="text-center text-slate-500 py-8">No requests</p>
                            ) : (
                              requests.map((req) => (
                                <Card key={req.id} className="bg-slate-50">
                                  <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <p className="font-medium">{req.rider?.name}</p>
                                        <p className="text-sm text-slate-500">{req.rider?.email}</p>
                                        <p className="text-sm mt-2">
                                          <MapPin className="w-3 h-3 inline mr-1" />
                                          Pickup: {req.pickup_location}
                                        </p>
                                        {req.message && (
                                          <p className="text-sm text-slate-600 mt-1 italic">"{req.message}"</p>
                                        )}
                                      </div>
                                      <Badge className={
                                        req.status === 'approved' ? 'bg-emerald-500' :
                                        req.status === 'rejected' ? 'bg-red-500' :
                                        'bg-amber-500'
                                      }>{req.status}</Badge>
                                    </div>
                                    {req.status === 'pending' && (
                                      <div className="flex gap-2 mt-4">
                                        <Button
                                          size="sm"
                                          className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                                          onClick={() => handleRequestAction(req.id, 'approved')}
                                        >
                                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-red-500 flex-1"
                                          onClick={() => handleRequestAction(req.id, 'rejected')}
                                        >
                                          <X className="w-4 h-4 mr-1" /> Reject
                                        </Button>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              ))
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Route Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingRoute ? 'Edit Route' : 'Create New Route'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Route Name *</Label>
                  <Input
                    value={formData.route_name}
                    onChange={(e) => setFormData(p => ({ ...p, route_name: e.target.value }))}
                    placeholder="e.g., School Run to St. Augustine's"
                    required
                    data-testid="route-name"
                  />
                </div>
                <div>
                  <Label>Route Type</Label>
                  <Select value={formData.route_type} onValueChange={(v) => setFormData(p => ({ ...p, route_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="school">School Run</SelectItem>
                      <SelectItem value="work">Work Commute</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max Riders</Label>
                  <Input
                    type="number"
                    min="1"
                    max="6"
                    value={formData.max_riders}
                    onChange={(e) => setFormData(p => ({ ...p, max_riders: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Start Location *</Label>
                  <Input
                    value={formData.start_location}
                    onChange={(e) => setFormData(p => ({ ...p, start_location: e.target.value }))}
                    placeholder="e.g., Killarney Heights"
                    required
                    data-testid="start-location"
                  />
                </div>
                <div className="col-span-2">
                  <Label>End Location *</Label>
                  <Input
                    value={formData.end_location}
                    onChange={(e) => setFormData(p => ({ ...p, end_location: e.target.value }))}
                    placeholder="e.g., Downtown Nassau"
                    required
                    data-testid="end-location"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Pickup Stops (comma-separated)</Label>
                  <Input
                    value={formData.stops}
                    onChange={(e) => setFormData(p => ({ ...p, stops: e.target.value }))}
                    placeholder="e.g., Gas Station, Shopping Plaza"
                  />
                </div>
                <div>
                  <Label>Departure Time *</Label>
                  <Input
                    value={formData.departure_time}
                    onChange={(e) => setFormData(p => ({ ...p, departure_time: e.target.value }))}
                    placeholder="e.g., 7:30 AM"
                    required
                    data-testid="departure-time"
                  />
                </div>
                <div>
                  <Label>Return Time</Label>
                  <Input
                    value={formData.return_time}
                    onChange={(e) => setFormData(p => ({ ...p, return_time: e.target.value }))}
                    placeholder="e.g., 3:30 PM"
                  />
                </div>
              </div>

              {/* Schedule Days */}
              <div>
                <Label>Schedule Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                    <Badge
                      key={day}
                      variant={formData.schedule_days.includes(day) ? 'default' : 'outline'}
                      className={`cursor-pointer ${formData.schedule_days.includes(day) ? 'bg-emerald-500' : ''}`}
                      onClick={() => toggleDay(day)}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Contribution */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <Label>Contribution Requirement</Label>
                <Select value={formData.contribution_type} onValueChange={(v) => setFormData(p => ({ ...p, contribution_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fuel">Fuel Contribution</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="negotiable">Negotiable</SelectItem>
                    <SelectItem value="free">Free Ride</SelectItem>
                  </SelectContent>
                </Select>
                {formData.contribution_type !== 'free' && (
                  <>
                    <Input
                      value={formData.contribution_amount}
                      onChange={(e) => setFormData(p => ({ ...p, contribution_amount: e.target.value }))}
                      placeholder="e.g., $5/week or Share fuel costs"
                    />
                    <Textarea
                      value={formData.contribution_details}
                      onChange={(e) => setFormData(p => ({ ...p, contribution_details: e.target.value }))}
                      placeholder="Additional details about contribution..."
                      rows={2}
                    />
                  </>
                )}
              </div>

              {/* Rules */}
              <div>
                <Label>Your Rules (Optional)</Label>
                <Textarea
                  value={formData.rules}
                  onChange={(e) => setFormData(p => ({ ...p, rules: e.target.value }))}
                  placeholder="e.g., No eating in car, Be on time, etc."
                  rows={2}
                />
              </div>

              <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-route-btn">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {editingRoute ? 'Update Route' : 'Create Route'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyRoutesPage;
